import React, { FC, useContext, useState } from 'react';
import { Alert, AlertTitle, Box, Divider, FormControlLabel, Switch, Typography } from '@mui/material';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { toggleApiEnabledTemplate } from 'apollo/templates/organization';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import {
    ApiClientsTable,
    OpenApiDocsButton,
    OrganizationRef,
    WebhookDeliveries,
    WebhookSubscriptions,
} from 'components/shared/api-client-management';

const LabApiClientSettings: FC = () => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const currentOrganization = currentOrgVar();
    const [isApiEnabled, setIsApiEnabled] = useState<boolean>(Boolean(currentOrganization?.isApiEnabled));
    const [confirmDisableOpen, setConfirmDisableOpen] = useState(false);

    const organization: OrganizationRef | null = currentOrganization?._id
        ? { _id: currentOrganization._id, name: currentOrganization.name ?? '' }
        : null;

    const [toggleApiEnabled] = useMutation(toggleApiEnabledTemplate, {
        update: (cache, { data }) => {
            if (data?.toggleApiEnabled?.isApiEnabled === false) {
                cache.evict({ fieldName: 'apiClientsForOrganization' });
                cache.evict({ fieldName: 'webhookSubscriptionsForOrganization' });
                cache.evict({ fieldName: 'webhookDeliveriesForOrganization' });
                cache.gc();
            }
        },
        onCompleted: ({ toggleApiEnabled: updated }) => {
            const next = Boolean(updated?.isApiEnabled);
            setIsApiEnabled(next);
            currentOrgVar({ ...currentOrganization, isApiEnabled: next });
            setConfirmDisableOpen(false);
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('common:CHANGES_SAVED'),
            });
        },
    });

    const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            toggleApiEnabled({ variables: { isApiEnabled: true } });
        } else {
            setConfirmDisableOpen(true);
        }
    };

    const handleConfirmDisable = () => {
        toggleApiEnabled({ variables: { isApiEnabled: false } });
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h1">{translate('app-settings:API_CLIENTS_AND_WEBHOOKS')}</Typography>
                {isApiEnabled && <OpenApiDocsButton organization={organization} />}
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>{translate('app-settings:API_FEATURE_INFO_TITLE')}</AlertTitle>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    {translate('app-settings:API_FEATURE_INFO_BODY')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                    {translate('app-settings:API_FEATURE_COST_WARNING')}
                </Typography>
            </Alert>

            <FormControlLabel
                control={
                    <Switch
                        color="primary"
                        checked={isApiEnabled}
                        onChange={handleSwitchChange}
                        data-test="api-feature-toggle"
                    />
                }
                label={translate('app-settings:API_FEATURE_ENABLE')}
            />

            {isApiEnabled && (
                <Box mt={3}>
                    <ApiClientsTable organization={organization} />

                    <Divider sx={{ my: 4 }} />

                    <WebhookSubscriptions organization={organization} />

                    <Divider sx={{ my: 4 }} />

                    <WebhookDeliveries organization={organization} />
                </Box>
            )}

            <ConfirmDialog
                open={confirmDisableOpen}
                handleClose={() => setConfirmDisableOpen(false)}
                handleConfirm={handleConfirmDisable}
                dialogTitle={translate('app-settings:API_FEATURE_DISABLE_CONFIRM_TITLE')}
                dialogContent={
                    <Typography variant="body2">
                        {translate('app-settings:API_FEATURE_DISABLE_CONFIRM_BODY')}
                    </Typography>
                }
                confirmBtnLabel={translate('app-settings:API_FEATURE_DISABLE_CONFIRM_BUTTON')}
            />
        </Box>
    );
};

export default LabApiClientSettings;
